import { ChevronLeft, Phone } from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useGetOne,
  useNotify,
  useLocaleState,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router";
import { Button, buttonVariants } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type {
  Apartment,
  CallResult,
  Contact,
  ContactNote,
  CrmEvent,
  DealCard,
  Sale,
  Task,
} from "../types";
import { formatTime, getCardStatus } from "./cardStatus";
import { LostDialog } from "./LostDialog";
import {
  fullName,
  useEventText,
  useFunnelStages,
  usePermissions,
  useRefreshFunnel,
  useStage,
} from "./hooks";
import { NativeSelect, StageBadge } from "./shared";
import { ApartmentPickerDialog } from "../apartments/ApartmentPickerDialog";
import { LeadScript } from "../scripts/LeadScript";
import { BuyerProfile } from "./BuyerProfile";
import { parseFormAnswers } from "./formAnswers";
import { nextTaskType, stageAfterCall } from "./leadLogic";

const RESULTS: CallResult[] = [
  "no_answer",
  "callback",
  "viewing_scheduled",
  "not_interested",
];

const Section = ({
  title,
  action,
  children,
  className,
}: {
  title: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <section
    className={cn("min-w-0 rounded-2xl border bg-card p-4 sm:p-5", className)}
  >
    <div className="mb-3 flex items-center justify-between gap-2">
      <h2 className="text-lg font-bold">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

export const LeadPage = () => {
  const { id } = useParams();
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const refresh = useRefreshFunnel();
  const {
    data: card,
    isPending,
    error,
  } = useGetOne<DealCard>("deal_cards", { id: id as Identifier });

  const back = (
    <Link
      to="/funnel"
      className="inline-flex items-center gap-1 text-sm font-semibold text-primary"
    >
      <ChevronLeft className="size-4" />
      {translate("crm.funnel.lead.back")}
    </Link>
  );

  if (isPending) return null;
  if (error || !card) {
    return (
      <div className="p-4">
        {back}
        <p className="mt-4 text-muted-foreground">
          {translate("crm.funnel.lead.not_found")}
        </p>
      </div>
    );
  }

  const content = (
    <div className="flex flex-col gap-4">
      {isMobile ? null : back}
      <LeadHeader card={card} onChange={refresh} />
      <div className="grid min-w-0 gap-4 lg:grid-cols-[1fr_320px]">
        <div className="flex min-w-0 flex-col gap-4">
          <CallResultForm card={card} onSaved={refresh} />
          <LeadScriptForCard card={card} />
          {card.contact_id != null ? (
            <BuyerProfile contactId={card.contact_id} />
          ) : null}
          {card.contact_id != null ? (
            <CommentsSection contactId={card.contact_id} />
          ) : null}
          <History contactId={card.contact_id} />
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <ContactSection card={card} onChange={refresh} />
          <ApartmentSection card={card} onChange={refresh} />
          <TasksSection card={card} onChange={refresh} />
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>{back}</MobileHeader>
        <MobileContent>{content}</MobileContent>
      </>
    );
  }
  return content;
};

LeadPage.path = "/funnel/:id";

const LeadHeader = ({
  card,
  onChange,
}: {
  card: DealCard;
  onChange: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const stages = useFunnelStages();
  const stage = useStage();
  const permissions = usePermissions();
  const [lostOpen, setLostOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const { data: sales = [] } = useGetList<Sale>(
    "sales",
    { pagination: { page: 1, perPage: 100 } },
    { enabled: transferOpen },
  );
  const name = fullName(card);
  const initials = name
    .split(" ")
    .map((part) => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const status = getCardStatus(card, new Date());
  const isLost = card.stage === "lost";
  const currentIndex = stages.findIndex((s) => s.value === card.stage);

  const setStage = async (value: string) => {
    if (value === card.stage) return;
    try {
      await dataProvider.update("deals", {
        id: card.id,
        data: { stage: value },
        previousData: card,
      });
      notify("crm.funnel.lead.stage_changed");
      onChange();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    }
  };

  // The lead and its deal move together; RLS and the transfer trigger check rights
  const transfer = async (salesId: string) => {
    if (!salesId) return;
    try {
      await dataProvider.update("deals", {
        id: card.id,
        data: { sales_id: Number(salesId) },
        previousData: card,
      });
      if (card.contact_id) {
        await dataProvider.update("contacts", {
          id: card.contact_id,
          data: { sales_id: Number(salesId) },
          previousData: { id: card.contact_id },
        });
      }
      notify("crm.funnel.lead.transferred");
      setTransferOpen(false);
      onChange();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    }
  };

  return (
    <section className="rounded-2xl border bg-card p-5">
      <div className="flex flex-wrap items-start gap-4">
        <div className="hidden size-14 items-center justify-center rounded-full bg-accent text-lg font-bold text-primary sm:flex">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
              {name}
            </h1>
            <StageBadge stage={stage(card.stage)} />
            {status?.key === "no_call" ? (
              <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive">
                {translate("crm.funnel.lead.no_call_badge")}
              </span>
            ) : null}
          </div>
          <div className="text-sm text-muted-foreground">
            {[
              card.phone,
              card.source
                ? translate(`resources.contacts.sources.${card.source}`)
                : null,
              card.source_form
                ? translate("crm.funnel.lead.form", { form: card.source_form })
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          {card.phone ? (
            <a
              href={`tel:${card.phone.replace(/[^\d+]/g, "")}`}
              className={cn(
                buttonVariants({ size: "lg" }),
                "flex-1 sm:flex-none",
              )}
            >
              <Phone />
              {translate("crm.funnel.lead.call")}
            </a>
          ) : null}
          {permissions.includes("leads.transfer") ? (
            transferOpen ? (
              <NativeSelect
                autoFocus
                defaultValue=""
                onChange={(event) => transfer(event.target.value)}
                onBlur={() => setTransferOpen(false)}
              >
                <option value="" disabled>
                  {translate("crm.funnel.lead.transfer")}
                </option>
                {sales
                  .filter((sale) => !sale.disabled && sale.id !== card.sales_id)
                  .map((sale) => (
                    <option key={sale.id} value={sale.id}>
                      {sale.first_name} {sale.last_name}
                    </option>
                  ))}
              </NativeSelect>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => setTransferOpen(true)}
              >
                {translate("crm.funnel.lead.transfer")}
              </Button>
            )
          ) : null}
        </div>
      </div>

      {/* Stage progress: click a step to move the deal */}
      <div className="mt-5 grid grid-cols-3 gap-2 sm:grid-cols-6">
        {stages.map((s, index) => {
          const reached = !isLost && index <= currentIndex;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => setStage(s.value)}
              className="flex flex-col gap-1 text-left text-xs font-semibold"
              aria-current={s.value === card.stage ? "step" : undefined}
            >
              <span
                className="h-2 rounded-full bg-muted"
                style={reached ? { backgroundColor: s.color } : undefined}
              />
              <span
                className={reached ? undefined : "text-muted-foreground"}
                style={s.value === card.stage ? { color: s.color } : undefined}
              >
                {s.label}
              </span>
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex justify-end">
        {isLost ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setStage("new")}
          >
            {translate("crm.funnel.lost_list.restore")}
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-destructive"
            onClick={() => setLostOpen(true)}
          >
            {translate("crm.funnel.lead.lose")}
          </Button>
        )}
      </div>
      <LostDialog
        deal={card}
        open={lostOpen}
        onOpenChange={setLostOpen}
        onLost={onChange}
      />
    </section>
  );
};

const CallResultForm = ({
  card,
  onSaved,
}: {
  card: DealCard;
  onSaved: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const stages = useFunnelStages();
  const [result, setResult] = useState<CallResult | null>(null);
  const [comment, setComment] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [when, setWhen] = useState("");
  const [saving, setSaving] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);

  const save = async () => {
    if (!result || !card.contact_id) {
      notify("crm.funnel.lead.choose_result", { type: "warning" });
      return;
    }
    setSaving(true);
    try {
      await dataProvider.create("calls", {
        data: {
          contact_id: card.contact_id,
          deal_id: card.id,
          result,
          comment: comment.trim() || null,
        },
      });
      if (nextAction.trim() || when) {
        await dataProvider.create("tasks", {
          data: {
            contact_id: card.contact_id,
            deal_id: card.id,
            type: nextTaskType[result],
            text: nextAction.trim(),
            due_date: when ? new Date(when).toISOString() : null,
          },
        });
      }
      const nextStage = stageAfterCall(
        card.stage,
        result,
        stages.map((s) => s.value),
      );
      if (nextStage !== card.stage) {
        await dataProvider.update("deals", {
          id: card.id,
          data: { stage: nextStage },
          previousData: card,
        });
      }
      notify("crm.funnel.lead.saved");
      setComment("");
      setNextAction("");
      setWhen("");
      if (result === "not_interested") setLostOpen(true);
      setResult(null);
      onSaved();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title={translate("crm.funnel.lead.call_result")}>
      <div className="flex flex-wrap gap-2">
        {RESULTS.map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={result === value}
            onClick={() => setResult(value)}
            className={cn(
              "rounded-xl border px-4 py-2.5 text-sm font-semibold",
              result === value
                ? "border-primary bg-accent text-primary"
                : "bg-card hover:border-primary/40",
            )}
          >
            {translate(`resources.calls.results.${value}`)}
          </button>
        ))}
      </div>
      <label className="mt-4 flex flex-col gap-1 text-sm font-semibold text-muted-foreground">
        {translate("crm.funnel.lead.comment")}
        <Textarea
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder={translate("crm.funnel.lead.comment_placeholder")}
        />
      </label>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="flex flex-1 flex-col gap-1 text-sm font-semibold text-muted-foreground">
          {translate("crm.funnel.lead.next_action")}
          <Input
            value={nextAction}
            onChange={(event) => setNextAction(event.target.value)}
            placeholder={translate("crm.funnel.lead.next_action_placeholder")}
          />
        </label>
        <label className="flex flex-1 flex-col gap-1 text-sm font-semibold text-muted-foreground">
          {translate("crm.funnel.lead.when")}
          <Input
            type="datetime-local"
            value={when}
            onChange={(event) => setWhen(event.target.value)}
          />
        </label>
        <Button type="button" onClick={save} disabled={saving || !result}>
          {translate("ra.action.save")}
        </Button>
      </div>
      <LostDialog
        deal={card}
        open={lostOpen}
        onOpenChange={setLostOpen}
        onLost={onSaved}
      />
    </Section>
  );
};

const History = ({ contactId }: { contactId: Identifier | null }) => {
  const translate = useTranslate();
  const eventText = useEventText();
  const { data: events = [] } = useGetList<CrmEvent>(
    "events",
    {
      filter: { contact_id: contactId },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "occurred_at", order: "DESC" },
    },
    { enabled: contactId != null },
  );
  const { data: calls = [] } = useGetList(
    "calls",
    {
      filter: { contact_id: contactId },
      pagination: { page: 1, perPage: 50 },
    },
    { enabled: contactId != null },
  );
  const commentOf = (event: CrmEvent) =>
    event.type === "call.logged"
      ? calls.find((call) => call.id === event.payload.call_id)?.comment
      : null;
  const when = (date: string) => {
    const d = new Date(date);
    const today = new Date().toDateString() === d.toDateString();
    return today
      ? formatTime(d)
      : `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  return (
    <Section title={translate("crm.funnel.lead.history")}>
      {events.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.funnel.lead.no_history")}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {events.map((event) => (
            <li key={event.id} className="flex gap-4 text-sm">
              <span className="w-12 shrink-0 text-xs text-muted-foreground">
                {when(event.occurred_at)}
              </span>
              <span>
                {eventText(event)}
                {commentOf(event) ? (
                  <span className="block text-muted-foreground">
                    {commentOf(event)}
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
};

/** The stage's script, with the client's objections from the profile first */
const LeadScriptForCard = ({ card }: { card: DealCard }) => {
  const { data: contact } = useGetOne<Contact>(
    "contacts",
    { id: card.contact_id as Identifier },
    { enabled: card.contact_id != null },
  );
  return (
    <LeadScript
      stage={card.stage}
      firstName={card.first_name}
      clientObjections={contact?.objections ?? []}
    />
  );
};

/** Form answers (the answer stands out) and the team's comments on the lead */
const CommentsSection = ({ contactId }: { contactId: Identifier }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const { data: notes = [], refetch } = useGetList<ContactNote>(
    "contact_notes",
    {
      filter: { contact_id: contactId },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "date", order: "ASC" },
    },
  );
  const { data: sales = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 200 },
  });
  const nameOf = (id: Identifier | null | undefined) => {
    const sale = sales.find((s) => s.id === id);
    return sale ? `${sale.first_name} ${sale.last_name}` : null;
  };
  const when = (date: string) =>
    new Date(date).toLocaleString("uk-UA", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await dataProvider.create("contact_notes", {
        data: {
          contact_id: contactId,
          text: text.trim(),
          date: new Date().toISOString(),
          sales_id: identity?.id,
        },
      });
      setText("");
      refetch();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title={translate("crm.funnel.lead.comments")}>
      {notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.funnel.lead.no_comments")}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {notes.map((note) => {
            const answers = parseFormAnswers(note.text);
            const author = nameOf(note.sales_id);
            return (
              <li
                key={note.id}
                className={cn(
                  "rounded-xl p-3 text-sm",
                  answers ? "bg-accent/60" : "bg-muted/50",
                )}
              >
                <div className="mb-1.5 text-xs text-muted-foreground">
                  {answers
                    ? translate("crm.funnel.lead.form_answers")
                    : (author ?? translate("crm.funnel.lead.system"))}{" "}
                  · {when(note.date)}
                </div>
                {answers ? (
                  <dl className="grid gap-2 sm:grid-cols-2">
                    {answers.map((item, index) => (
                      <div key={index}>
                        <dt className="text-xs text-muted-foreground">
                          {item.question}
                        </dt>
                        <dd className="text-base font-bold">{item.answer}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="whitespace-pre-line">{note.text}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <div className="mt-3 flex flex-col gap-2">
        <Textarea
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={translate("crm.funnel.lead.note_placeholder")}
          rows={2}
        />
        <Button
          type="button"
          variant="outline"
          className="self-end"
          onClick={add}
          disabled={saving || !text.trim()}
        >
          {translate("crm.funnel.lead.add_comment")}
        </Button>
      </div>
    </Section>
  );
};

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex items-baseline justify-between gap-2 py-1 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right font-semibold">{children}</span>
  </div>
);

const ContactSection = ({ card }: { card: DealCard; onChange: () => void }) => {
  const translate = useTranslate();
  const { data: manager } = useGetOne<Sale>(
    "sales",
    { id: card.sales_id },
    { enabled: card.sales_id != null },
  );
  const [locale] = useLocaleState();
  const created = card.lead_created_at ? new Date(card.lead_created_at) : null;
  return (
    <Section title={translate("crm.funnel.lead.contact")}>
      <Row label={translate("crm.funnel.lead.phone")}>{card.phone ?? "—"}</Row>
      <Row label={translate("crm.funnel.lead.source")}>
        {card.source
          ? translate(`resources.contacts.sources.${card.source}`)
          : "—"}
      </Row>
      <Row label={translate("crm.funnel.lead.created")}>
        {created
          ? `${created.toLocaleDateString(locale, { day: "numeric", month: "long" })}, ${formatTime(created)}`
          : "—"}
      </Row>
      <Row label={translate("crm.funnel.lead.manager")}>
        {manager ? `${manager.first_name} ${manager.last_name}` : "—"}
      </Row>
    </Section>
  );
};

const statusClass: Record<string, string> = {
  free: "bg-accent text-primary",
  booked: "bg-honey/20 text-warning",
  sold: "bg-destructive/10 text-destructive",
  off: "bg-muted text-muted-foreground",
};

const ApartmentSection = ({
  card,
  onChange,
}: {
  card: DealCard;
  onChange: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [locale] = useLocaleState();
  const [editing, setEditing] = useState(false);
  const { data: apartment } = useGetOne<Apartment>(
    "apartments",
    { id: card.apartment_id as Identifier },
    { enabled: card.apartment_id != null },
  );
  const choose = async (value: string) => {
    try {
      await dataProvider.update("deals", {
        id: card.id,
        data: { apartment_id: value ? Number(value) : null },
        previousData: card,
      });
      setEditing(false);
      onChange();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    }
  };
  return (
    <Section
      title={translate("crm.funnel.lead.apartment")}
      action={
        <button
          type="button"
          className="text-sm font-semibold text-primary underline"
          onClick={() => setEditing(!editing)}
        >
          {translate("crm.funnel.lead.change")}
        </button>
      }
    >
      <ApartmentPickerDialog
        open={editing}
        onOpenChange={setEditing}
        current={card.apartment_id}
        roomsWanted={card.rooms_wanted}
        onPick={(id) => choose(id == null ? "" : String(id))}
      />
      {apartment ? (
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="font-bold">
              {translate("crm.funnel.lead.section_number", {
                section: apartment.section,
                number: apartment.number,
              })}
            </div>
            <div className="text-xs text-muted-foreground">
              {translate("crm.funnel.lead.apartment_line", {
                floor: apartment.floor,
                rooms: apartment.rooms,
              })}
              {apartment.total_area
                ? ` · ${Number(apartment.total_area).toLocaleString(locale)} м²`
                : ""}
            </div>
          </div>
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-xs font-semibold",
              statusClass[apartment.status],
            )}
          >
            {translate(`resources.apartments.statuses.${apartment.status}`)}
          </span>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {translate("crm.funnel.lead.no_apartment")}
        </p>
      )}
    </Section>
  );
};

const TasksSection = ({
  card,
  onChange,
}: {
  card: DealCard;
  onChange: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { taskTypes } = useConfigurationContext();
  const [locale] = useLocaleState();
  const [text, setText] = useState("");
  const [due, setDue] = useState("");
  const { data: tasks = [] } = useGetList<Task>(
    "tasks",
    {
      filter: { contact_id: card.contact_id, "done_date@is": null },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "due_date", order: "ASC" },
    },
    { enabled: card.contact_id != null },
  );
  const done = async (task: Task) => {
    await dataProvider.update("tasks", {
      id: task.id,
      data: { done_date: new Date().toISOString() },
      previousData: task,
    });
    onChange();
  };
  const add = async () => {
    if (!text.trim() || !card.contact_id) return;
    try {
      await dataProvider.create("tasks", {
        data: {
          contact_id: card.contact_id,
          deal_id: card.id,
          type: "none",
          text: text.trim(),
          due_date: due ? new Date(due).toISOString() : null,
        },
      });
      setText("");
      setDue("");
      notify("crm.funnel.lead.task_added");
      onChange();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    }
  };
  const label = (task: Task) => {
    const type = taskTypes.find((t) => t.value === task.type)?.label;
    const what = task.text || type || "";
    return task.due_date
      ? `${what} · ${new Date(task.due_date).toLocaleDateString(locale, { day: "2-digit", month: "2-digit" })} ${formatTime(new Date(task.due_date))}`
      : what;
  };
  return (
    <Section title={translate("crm.funnel.lead.tasks")}>
      {tasks.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.funnel.lead.no_tasks")}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {tasks.map((task) => (
            <li key={task.id} className="flex items-center gap-3 text-sm">
              <Checkbox
                aria-label={label(task)}
                onCheckedChange={() => done(task)}
              />
              <span>{label(task)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-4 flex flex-col gap-2">
        <Input
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={translate("crm.funnel.lead.task_text")}
        />
        <div className="flex flex-col gap-2">
          <Input
            type="datetime-local"
            value={due}
            onChange={(event) => setDue(event.target.value)}
          />
          <Button
            type="button"
            variant="outline"
            onClick={add}
            disabled={!text.trim()}
          >
            {translate("crm.funnel.lead.add_task")}
          </Button>
        </div>
      </div>
    </Section>
  );
};
